import { lazy, Suspense, useEffect } from 'react'

import { createFileRoute, redirect } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import * as Sentry from '@sentry/react'

import { AskAIButton } from '@/features/search/ai/components/ask-ai-button'
import { useSearchPageContext } from '@/features/search/ai/hooks/use-search-page-context'

import '@/features/search/ai/register'

import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import {
	Section,
	SectionDescription,
	SectionTitle,
} from '@/features/app-shell/section'
import { integrationQueries } from '@/features/integrations/queries'
import {
	IntegrationSelectSkeleton,
	SearchSkeleton,
	TimeRangeSkeleton,
} from '@/features/search/components/skeletons'
import { searchLogsQueries } from '@/features/search/queries'
import { searchSchema } from '@/features/search/types'

import { ROLES } from '@/shared/auth/constants'
import {
	getCurrentRole,
	getCurrentSKU,
	getCurrentTenant,
} from '@/shared/auth/store'
import { hasActiveSKU } from '@/shared/auth/utils/sku'
import { hasRequiredRole } from '@/shared/auth/utils/user'
import { useContainerWidth } from '@/shared/layout/hooks/use-container-width'
import { redirectHome } from '@/shared/routing/utils/redirect'

import { ErrorBoundary } from '@/components/common/error-boundary'
import { Skeleton } from '@/components/ui/skeleton'

const QueryInput = lazy(() => import('@/features/search/components/input'))
const SearchFilters = lazy(
	() => import('@/features/search/components/search-filters'),
)
const TimeRange = lazy(() => import('@/features/search/components/time-range'))
const IntegrationSelect = lazy(
	() => import('@/features/search/components/integration-select'),
)
const SearchSidebar = lazy(() => import('@/features/search/components/sidebar'))
const SearchResults = lazy(() => import('@/features/search/components/results'))

export const Route = createFileRoute('/_dashboard/search/')({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: () => {
		const sku = getCurrentSKU()
		const role = getCurrentRole()

		// Redirect if the SKU is not search enabled
		if (sku && !sku.is_search_enabled) redirectHome()

		// Redirect if the user does not have the required role
		if (
			(role && !hasRequiredRole(role, ROLES.Analyst)) ||
			(sku && !hasActiveSKU(sku))
		)
			throw redirect({ to: '/unauthorized' })
	},

	loader: ({ context: { queryClient } }) => {
		const tenant = getCurrentTenant()

		if (tenant) {
			// Prefetch integration data for Log Source and Event Type selectors
			Promise.all([
				queryClient.prefetchQuery(integrationQueries.list(tenant)),
				queryClient.prefetchQuery(integrationQueries.available(tenant)),
				queryClient.prefetchQuery(searchLogsQueries.savedSearchList(tenant)),
			])
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Search | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: SearchSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { ref, width } = useContainerWidth()
	const pageContext = useSearchPageContext()
	const searchState = Route.useSearch()

	// Stamp a Sentry breadcrumb on every URL state change so error reports
	// from the route carry the user's active search context.
	useEffect(() => {
		Sentry.addBreadcrumb({
			category: 'search',
			message: 'navigate',
			level: 'info',
			data: {
				integrations_count: searchState.int?.length ?? 0,
				has_query: !!searchState.q?.rules && searchState.q.rules.length > 0,
				filters_count: searchState.ft?.length ?? 0,
				time_range_kind:
					searchState.tr && 'preset' in searchState.tr
						? 'preset'
						: searchState.tr
							? 'absolute'
							: 'none',
			},
		})
	}, [searchState])

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section ref={ref}>
				<div className="flex w-full items-start justify-between gap-4">
					<div className="flex flex-col gap-1">
						<SectionTitle>Search</SectionTitle>
						<SectionDescription>
							Search, filter, and investigate log data across your connected
							integrations to quickly identify suspicious activity and security
							events.
						</SectionDescription>
					</div>
					<AskAIButton />
				</div>

				<div className="flex w-full flex-col items-start justify-start gap-4">
					<div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row">
						<ErrorBoundary>
							<Suspense fallback={<IntegrationSelectSkeleton />}>
								<IntegrationSelect />
							</Suspense>
						</ErrorBoundary>

						<Suspense fallback={<TimeRangeSkeleton />}>
							<TimeRange />
						</Suspense>
					</div>

					<Suspense fallback={<Skeleton className="h-12 w-full" />}>
						<QueryInput />
					</Suspense>

					<Suspense fallback={<Skeleton className="h-10 w-full" />}>
						<SearchFilters />
					</Suspense>

					<div className="flex w-full flex-row items-start justify-start gap-6">
						<ErrorBoundary>
							<Suspense
								fallback={<Skeleton className="h-full min-h-96 w-72" />}
							>
								<SearchSidebar parentRef={ref} parentWidth={width} />
							</Suspense>
						</ErrorBoundary>

						<ErrorBoundary>
							<Suspense
								fallback={<Skeleton className="h-full min-h-96 w-full" />}
							>
								<SearchResults />
							</Suspense>
						</ErrorBoundary>
					</div>
				</div>
			</Section>
		</AIRailLayout>
	)
}
